import { ColumnDef, Row } from "@tanstack/react-table";
import { TaxRate } from "@/app/types/taxRate";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Edit, Trash2 } from "lucide-react";
import { DataTableColumnHeader } from "@/components/ui/data-table-column-header";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Define filterable columns for the data table
export const filterableColumns = [
  {
    id: "is_active",
    title: "Status",
    options: [
      { label: "Active", value: "true" },
      { label: "Inactive", value: "false" },
    ],
  },
  {
    id: "organization_id",
    title: "Type",
    options: [
      { label: "Global", value: "null" },
      { label: "Organization", value: "not_null" },
    ],
  },
];

export const columns: ColumnDef<TaxRate>[] = [
  {
    accessorKey: "name",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
  },
  {
    accessorKey: "code",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Code" />,
    cell: ({ row }) => <span className="font-mono">{row.original.code}</span>,
  },
  {
    accessorKey: "rate",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Total Rate (%)" />,
    cell: ({ row }) => <span className="font-mono">{row.original.rate}%</span>,
  },
  {
    accessorKey: "components",
    header: "Components",
    cell: ({ row }): JSX.Element | string => {
      const components = row.original.components;
      if (!components || components.length === 0) {
        return <span className="text-muted-foreground">-</span>;
      }
      return (
        <div className="flex flex-wrap gap-1">
          {components.map((compDetail) => (
             <TooltipProvider key={compDetail.id}>
                <Tooltip>
                    <TooltipTrigger asChild>
                         <Badge variant="secondary" className="whitespace-nowrap">
                           {compDetail.tax_component.code}: {compDetail.rate}%
                         </Badge>
                    </TooltipTrigger>
                    <TooltipContent>
                       <p>{compDetail.tax_component.name}</p>
                    </TooltipContent>
                </Tooltip>
             </TooltipProvider>
          ))}
        </div>
      );
    },
    enableSorting: false,
  },
  {
    accessorKey: "is_active",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Status" />,
    cell: ({ row }): JSX.Element => (
      row.original.is_active ? (
        <Badge className="bg-success">Active</Badge>
      ) : (
        <Badge variant="destructive">Inactive</Badge>
      )
    ),
    filterFn: (row: Row<TaxRate>, id: string, value: string[]): boolean => {
      if (!value.length) return true;
      const isActive = row.original.is_active;
      return value.includes(isActive ? "true" : "false");
    },
  },
  {
    accessorKey: "organization_id",
    header: ({ column }) => <DataTableColumnHeader column={column} title="Type" />,
    cell: ({ row }): JSX.Element => (
      row.original.organization_id ? (
        <Badge variant="secondary">Organization</Badge>
      ) : (
        <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">Global</Badge>
      )
    ),
    filterFn: (row: Row<TaxRate>, id: string, value: string[]): boolean => {
      if (!value.length) return true;
      const orgId = row.original.organization_id;
      if (value.includes("null") && orgId === null) return true;
      if (value.includes("not_null") && orgId !== null) return true;
      return false;
    },
  },
  {
    id: "actions",
    cell: ({ row }): JSX.Element => {
      const taxRate = row.original;
      const isGlobal = !taxRate.organization_id;
      
      // Custom event dispatcher
      const dispatchEvent = (eventName: string, detail: TaxRate): void => {
        const event = new CustomEvent(eventName, { detail });
        document.dispatchEvent(event);
      };
      
      return (
        <div className="flex items-center justify-end gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => dispatchEvent("edit-taxrate", taxRate)}
                  disabled={isGlobal}
                >
                  <Edit className="h-4 w-4" />
                  <span className="sr-only">Edit</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{isGlobal ? "Cannot edit global tax rates" : "Edit tax rate"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => dispatchEvent("delete-taxrate", taxRate)}
                  disabled={isGlobal}
                >
                  <Trash2 className="h-4 w-4" />
                  <span className="sr-only">Delete</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>{isGlobal ? "Cannot delete global tax rates" : "Delete tax rate"}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      );
    },
  },
]; 